/**
 * An info icon with a short tooltip, the tooltip body every such tip
 * uses, and the label a form field carries one beside.
 *
 * A field's tip is its description: the text also sits in the page,
 * visually hidden under `tipId(fieldId)`, so the field names it in
 * `aria-describedby` and a screen reader reads it with the field. The
 * tooltip itself only renders while open.
 */

import { Info } from 'lucide-react'

import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { tipId } from '@/lib/describedBy'

/**
 * The tooltip renders in a portal, so it sets its font,
 * case, weight and tracking itself: a column header is capitals in the
 * code font and a group name is a heading, and the note must be neither.
 */
export function TipContent({ text }: { text: string }): React.JSX.Element {
  return (
    <TooltipContent
      side="top"
      className="font-sans text-sm font-normal normal-case tracking-normal"
    >
      <p className="text-foreground">{text}</p>
    </TooltipContent>
  )
}

export function InfoTip({
  text,
  label = text,
  id,
}: {
  text: string
  /** What the button is called. The text itself unless the tip describes a field. */
  label?: string
  /** Where the text also sits, hidden, for a field's `aria-describedby`. */
  id?: string
}): React.JSX.Element {
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            aria-describedby={id}
            className="inline-flex cursor-help align-middle text-muted-foreground hover:text-foreground"
          >
            <Info className="size-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TipContent text={text} />
      </Tooltip>
      {id !== undefined && (
        <span id={id} className="sr-only">
          {text}
        </span>
      )}
    </>
  )
}

/** A field's label, with its tip beside it when it has one. */
export function FieldLabel({
  htmlFor,
  label,
  tip,
}: {
  htmlFor: string
  label: string
  tip?: string
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {tip !== undefined && <InfoTip text={tip} label={`About ${label}`} id={tipId(htmlFor)} />}
    </div>
  )
}
